import './deformation-img.less';
import React, { useEffect, useRef, useState } from 'react';
import IconFont from '@meitu/xdesign-icon-font';
import { BaseStagePlugin, getFramework, useActiveOriginIndex, useIdentity, useScale } from '@/framework';
import { getLocaleStore } from '@/i18n';
import { IExportPlugin, PluginType } from '@/version-types';
import { checkScaleKeydown } from '../hotkey-event/scale/utils';
import DeformationImgWrap from './components/deformation-img-wrap';
import { DeformationImgPlugin } from './deformation-img';
import deformationImgPluginState, { useDeformationImgState } from './states/deformation-img';
import { EDeformationEvent } from './types';

const DeformationImgPluginView = React.memo(() => {
    const { $t } = getLocaleStore();
    const identity = useIdentity();
    const framework = getFramework(identity);
    const scale = useScale();
    const isOperation = useDeformationImgState()((state) => state.isOperation);
    const activeOriginIndex = useActiveOriginIndex();
    const isDeformationImage = useDeformationImgState()((state) => state.isDeformationImage);
    const nodeRef = useRef<HTMLDivElement>(null);
    const wheelY = useRef<number>(0);
    const [refresh, setRefresh] = useState(Math.random());
    const isSaving = useDeformationImgState()((state) => state.isSaving);

    // 裁剪的时候也要可以滚轮缩放
    useEffect(() => {
        if (!isDeformationImage) return;
        const mouseWheelHandle = (ev) => {
            const isScaleKeyDown = ev.ctrlKey || checkScaleKeydown();
            if (!isScaleKeyDown) return;
            ev.preventDefault();
            wheelY.current += ev.deltaY;
            if (Math.abs(wheelY.current) >= 4) {
                if (ev.deltaY > 0) {
                    wheelY.current = 0;
                    framework.stage.zoomOut();
                } else if (ev.deltaY < 0) {
                    wheelY.current = 0;
                    framework.stage.zoomIn();
                }
            }
        };
        nodeRef.current?.addEventListener('wheel', mouseWheelHandle);
        return () => {
            nodeRef.current?.removeEventListener('wheel', mouseWheelHandle);
        };
    }, [isDeformationImage]);

    if (!isDeformationImage) return null;

    const getConfirmPosition = () => {
        const elements = document.querySelectorAll('.c-deformation-img-operate-point');

        if (elements.length === 0) {
            setTimeout(() => {
                setRefresh(Math.random());
            }, 100);
            return {
                position: 'fixed',
                left: '0',
                top: '0',
            } as React.CSSProperties;
        }

        const x = [] as number[];
        const y = [] as number[];

        for (let i = 0; i < elements.length; i++) {
            const element = elements[i];
            const rect = element.getBoundingClientRect();
            x.push(rect.left);
            y.push(rect.top);
        }

        const minX = Math.min(...x);
        const minY = Math.min(...y);
        const maxX = Math.max(...x);
        const maxY = Math.max(...y);
        const style = {
            position: 'fixed',
            left: `${minX}px`,
            top: `${minY - 50}px`,
        } as React.CSSProperties;

        if (minY - 50 < 54) {
            style.top = `${maxY + 20}px`;
        }

        return style;
    };

    return (
        <div
            className="deformation-img-plugin-wrap"
            id="deformation-img-plugin-wrap"
            ref={nodeRef}
            style={{ opacity: isSaving ? 0 : 1 }}
        >
            {!isOperation && (
                <div className="l-operate-stage_confirm" key={refresh} style={getConfirmPosition()}>
                    <div
                        className="btn-cancel"
                        onClick={() => {
                            framework.events.emit(EDeformationEvent.CANCEL);
                        }}
                    >
                        <IconFont name="CrossCircle" fontSize={16} color="var(--content-system-primary, #1C1D1F)" />
                        <div>{$t((v) => v.collapse.adjustCollapse.cancelText)}</div>
                    </div>
                    <div
                        className="btn-confirm"
                        onClick={async () => {
                            framework.events.emit(EDeformationEvent.INNER_CONFIRM);
                        }}
                    >
                        <IconFont name="CheckCircle" fontSize={16} color="var(--content-tag)" />
                        <div>{$t((v) => v.collapse.adjustCollapse.okText)}</div>
                    </div>
                </div>
            )}

            <BaseStagePlugin originIndexOrLayerId={activeOriginIndex} name="deformation-img-plugin">
                <DeformationImgWrap scale={scale} framework={framework} />
            </BaseStagePlugin>
        </div>
    );
});

const exportPlugin: IExportPlugin = {
    type: PluginType.STAGE,
    sort: 9,
    name: 'deformation-img-plugin',
    render() {
        return <DeformationImgPluginView />;
    },
    createState() {
        return [deformationImgPluginState];
    },
    createInstance({ identity }) {
        return [{ key: 'DeformationImgPlugin', value: new DeformationImgPlugin(identity) }];
    },
};

export default exportPlugin;
